import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlayCircle, Clock, BarChart3, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { getEnrolledCourses, COURSES } from '@/lib/data';

export default function MyCourses() {
  const enrolled = getEnrolledCourses();
  const enriched = enrolled.map(e => ({
    ...e,
    course: COURSES.find(c => c.id === e.courseId),
  })).filter(e => e.course);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">My Courses</h1>
          <p className="text-muted-foreground text-sm mt-1 font-body">
            {enrolled.length} course{enrolled.length !== 1 ? 's' : ''} enrolled
          </p>
        </div>
        {enrolled.length > 0 && (
          <Link to="/courses">
            <Button variant="outline" size="sm" className="glass rounded-xl text-sm font-medium border-black/10">
              Browse More
            </Button>
          </Link>
        )}
      </div>

      {enriched.length > 0 ? (
        <div className="grid sm:grid-cols-2 gap-5">
          {enriched.map((item, i) => (
            <motion.div
              key={item.courseId}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="glass rounded-2xl border border-white/80 overflow-hidden shadow-sm shadow-black/5 group"
            >
              {/* Course banner */}
              <div className="relative h-28 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent overflow-hidden">
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-heading text-5xl font-black text-primary/20 select-none">
                    {item.course.title[0]}
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full glass border-black/10">
                    {item.course.level}
                  </span>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <h3 className="font-heading font-bold text-base leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                  {item.course.title}
                </h3>
                <div className="flex items-center gap-4 text-xs text-muted-foreground font-body">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-primary" />
                    {item.course.duration}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <BarChart3 className="w-3.5 h-3.5 text-primary" />
                    {item.course.level}
                  </span>
                </div>

                {/* Progress */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-medium">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="text-foreground">{item.progress}%</span>
                  </div>
                  <Progress value={item.progress} className="h-1.5 rounded-full" />
                </div>

                <Link to={`/player/${item.courseId}`}>
                  <Button className="w-full gap-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md shadow-primary/20 mt-1">
                    <PlayCircle className="w-4 h-4" />
                    {item.progress > 0 ? 'Resume Learning' : 'Start Learning'}
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-24 glass rounded-3xl border border-white/80 shadow-sm"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5"
          >
            <Sparkles className="w-8 h-8 text-primary" />
          </motion.div>
          <h3 className="font-heading font-bold text-xl">No courses yet</h3>
          <p className="text-muted-foreground text-sm mt-2 font-body">Browse our catalog and start learning today!</p>
          <Link to="/courses">
            <Button className="mt-6 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/25 font-semibold px-8">
              Browse Courses
            </Button>
          </Link>
        </motion.div>
      )}
    </motion.div>
  );
}